import type { BulkActionAvailabilityResult, BulkActionValidator } from "./types";

/**
 * Generic function that checks bulk action availability for a list of entities.
 *
 * @param reasonKeys - All possible reason keys (used to initialize the record with empty arrays)
 * @param validator  - The validator function for this specific action
 * @param entities   - The entities to validate
 * @param extraArgs  - Any extra arguments to pass to the validator
 */
export function checkBulkActionAvailability<Entity, Reason extends string, ExtraArgs extends unknown[] = []>(
	reasonKeys: readonly Reason[],
	validator: BulkActionValidator<Entity, Reason, ExtraArgs>,
	entities: Entity[],
	...extraArgs: ExtraArgs
): BulkActionAvailabilityResult<Entity, Reason> {
	const available: Entity[] = [];
	const unavailable = Object.fromEntries(reasonKeys.map((k) => [k, [] as Entity[]])) as Record<
		Reason,
		Entity[]
	>;

	for (const entity of entities) {
		const [valid, reason] = validator(entity, ...extraArgs);
		if (valid) {
			available.push(entity);
		} else {
			unavailable[reason].push(entity);
		}
	}

	if (available.length === entities.length) {
		return [available, null];
	}

	return [available, unavailable];
}
